import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Users, Phone, Building, Trash2, X, Upload, FileText, Download, Mail, UserPlus, Check, XCircle, LogOut, Crown, Network, TrendingUp, CheckCircle, MessageCircle, Bell, Zap, BarChart2, Camera, CalendarDays } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';
import { ContactDetailModal } from '../components/ContactDetailModal';
import { Organigrama } from '../components/Organigrama';
import { VentasDashboard } from '../components/VentasDashboard';
import { ManageUsers } from '../components/ManageUsers';
import { Licenses } from '../components/Licenses';
import { TeamChatModal } from '../components/TeamChatModal';
import { OrgRules } from '../components/OrgRules';
import { SeccionIdealista } from '../components/SeccionIdealista';
import { SeccionLlamadas } from '../components/SeccionLlamadas';
import { SeccionMail } from '../components/SeccionMail';
import { SeccionWhatsApp } from '../components/SeccionWhatsApp';
import { Calendario } from '../components/Calendario';
import { ProveedorTelefono, BotonesLlamar } from '../components/Softphone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Idealista y Llamadas son propias de E-Home (es donde vive el agente setter y
// donde hay alguien que llama). En el resto de organizaciones ni se pintan: una
// pestaña vacia que no puede llenarse nunca es peor que no tenerla.
const ORG_CON_CAPTACION = 'org_108f03b7aa81';

/* ── Animation helpers ──────────────────────────────────── */
const fadeUp   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25,0.46,0.45,0.94] } } };
const stagger  = { show: { transition: { staggerChildren: 0.07 } } };
const pageEnter = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25,0.46,0.45,0.94] } } };

/* ── SpotCard — spotlight hover effect ─────────────────── */
const SpotCard = ({ children, className = '', onClick }) => {
  const ref = useRef(null);
  const move = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mx', `${e.clientX - r.left}px`);
    ref.current.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={move} onClick={onClick}
      style={{ '--mx':'50%', '--my':'50%' }}
      className={`relative overflow-hidden transition-all duration-300 ${className}`}
      css={undefined}>
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: 'radial-gradient(400px circle at var(--mx) var(--my), rgba(201,168,76,0.06), transparent 60%)' }} />
      {children}
    </div>
  );
};

export const CRM = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('organizations');
  // Numero cuyo chat hay que abrir cuando se pulsa el icono de WhatsApp en una
  // ficha. Va por evento y no por prop porque la tarjeta esta muy abajo en el
  // arbol y no queria arrastrar un callback por seis niveles.
  const [chatAAbrir, setChatAAbrir] = useState(null);
  useEffect(() => {
    const ir = (e) => {
      setChatAAbrir(e.detail?.wa_id || null);
      setActiveTab('whatsapp');
    };
    window.addEventListener('ehome:abrir-whatsapp', ir);
    return () => window.removeEventListener('ehome:abrir-whatsapp', ir);
  }, []);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTeamChat, setShowTeamChat] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [orgStats, setOrgStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [uploadingOrgPic, setUploadingOrgPic] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all'); // all|month|week|day
  const [expandedAgent, setExpandedAgent] = useState(null);

  useEffect(() => {
    if (activeTab === 'organizations') {
      loadOrganizations();
      loadPendingInvites();
    } else if (activeTab === 'sellers' && selectedOrg) {
      loadSellers();
    } else if (activeTab === 'buyers' && selectedOrg) {
      loadBuyers();
    } else if (activeTab === 'properties' && selectedOrg) {
      loadProperties();
    } else if (activeTab === 'professionals' && selectedOrg) {
      loadProfessionals();
    }

    const interval = setInterval(() => {
      if (activeTab === 'organizations') loadPendingInvites();
      else if (activeTab === 'sellers' && selectedOrg) loadSellers();
      else if (activeTab === 'buyers' && selectedOrg) loadBuyers();
      else if (activeTab === 'properties' && selectedOrg) loadProperties();
      else if (activeTab === 'professionals' && selectedOrg) loadProfessionals();
    }, 15000);
    return () => clearInterval(interval);
  }, [activeTab, selectedOrg]);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`);
      setOrganizations(res.data || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar organizaciones');
    }
    setLoading(false);
  };

  const loadPendingInvites = async () => {
    try {
      const res = await axios.get(`${API}/organizations/invites/pending`);
      setPendingInvites(res.data || []);
    } catch (error) {
      console.error('Error loading invites:', error);
    }
  };

  const handleAcceptInvite = async (organization_id) => {
    try {
      await axios.post(`${API}/organizations/${organization_id}/accept`, {});
      toast.success('✅ Te has unido a la organización');
      loadPendingInvites();
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar');
    }
  };

  const handleDeclineInvite = async (organization_id) => {
    try {
      await axios.post(`${API}/organizations/${organization_id}/decline`, {});
      toast.success('Invitación rechazada');
      loadPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    }
  };

  // Map activeTab → backend path (backend usa /crm/ prefix y nombres específicos)
  const getBackendPath = (tab) => {
    if (tab === 'properties') return 'crm/properties-captured';
    return `crm/${tab}`;
  };

  const loadSellers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/sellers?org_id=${selectedOrg.org_id}`);
      setSellers(res.data || []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const loadBuyers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/buyers?org_id=${selectedOrg.org_id}`);
      setBuyers(res.data || []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const loadProperties = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/properties-captured?org_id=${selectedOrg.org_id}`);
      setProperties(res.data || []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const loadStats = async (org, period = 'all') => {
    setLoadingStats(true);
    try {
      const q = period !== 'all' ? `?period=${period}` : '';
      const res = await axios.get(`${API}/organizations/${org.org_id}/stats${q}`);
      setOrgStats(res.data);
    } catch {
      toast.error('Error al cargar estadísticas');
    }
    setLoadingStats(false);
  };

  const loadProfessionals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/professionals?org_id=${selectedOrg.org_id}`);
      setProfessionals(res.data || []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'sellers': return sellers;
      case 'buyers': return buyers;
      case 'properties': return properties;
      case 'professionals': return professionals;
      default: return [];
    }
  };

  const getIdField = () => {
    switch (activeTab) {
      case 'sellers': return 'seller_id';
      case 'buyers': return 'buyer_id';
      case 'properties': return 'captured_id';
      case 'professionals': return 'professional_id';
      default: return 'id';
    }
  };

  const handleDelete = async (id, tab, nombre) => {
    // Decir QUE se borra, no un "¿seguro?" a ciegas: es la diferencia entre
    // confirmar y aceptar por inercia.
    const que = { sellers: 'el vendedor', buyers: 'el comprador',
                  properties: 'la propiedad', professionals: 'el profesional' }[tab] || 'la ficha';
    if (!window.confirm(`Eliminar ${que} ${nombre ? `"${nombre}"` : ''}?\n\nNo se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/${getBackendPath(tab)}/${id}`);
      toast.success('Eliminado');
      if (tab === 'sellers') loadSellers();
      if (tab === 'buyers') loadBuyers();
      if (tab === 'properties') loadProperties();
      if (tab === 'professionals') loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'nuevo': 'bg-blue-500',
      'contactado': 'bg-yellow-500',
      'negociando': 'bg-orange-500',
      'cerrado': 'bg-green-500',
      'perdido': 'bg-red-500'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-500';
  };

  const handleCreateOrg = async () => {
    try {
      await axios.post(`${API}/organizations`, formData);
      toast.success('Organización creada');
      setFormData({});
      setShowForm(false);
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm('¿Eliminar esta organización? Se perderán todos sus datos.')) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}`);
      toast.success('Organización eliminada');
      loadOrganizations();
      if (selectedOrg?.org_id === orgId) {
        setSelectedOrg(null);
        setActiveTab('organizations');
      }
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleLeaveOrg = async (orgId) => {
    if (!window.confirm('¿Salir de esta organización?')) return;
    try {
      await axios.post(`${API}/organizations/${orgId}/leave`, {});
      toast.success('Has salido de la organización');
      loadOrganizations();
      if (selectedOrg?.org_id === orgId) {
        setSelectedOrg(null);
        setActiveTab('organizations');
      }
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleUploadDocument = async (itemId, file) => {
    setUploadingDoc(itemId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/${getBackendPath(activeTab)}/${itemId}/documents`, formData);
      toast.success('Documento subido');
      if (activeTab === 'sellers') loadSellers();
      if (activeTab === 'buyers') loadBuyers();
      if (activeTab === 'properties') loadProperties();
      if (activeTab === 'professionals') loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir');
    }
    setUploadingDoc(null);
  };

  const handleDeleteDocument = async (itemId, docUrl) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try {
      await axios.delete(`${API}/${getBackendPath(activeTab)}/${itemId}/documents?doc_url=${encodeURIComponent(docUrl)}`);
      toast.success('Documento eliminado');
      if (activeTab === 'sellers') loadSellers();
      if (activeTab === 'buyers') loadBuyers();
      if (activeTab === 'properties') loadProperties();
      if (activeTab === 'professionals') loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...formData, org_id: selectedOrg?.org_id };
      if (activeTab === 'professionals' && payload.telefono && !payload.phone) {
        payload.phone = payload.telefono;
        delete payload.telefono;
      }
      await axios.post(`${API}/${getBackendPath(activeTab)}`, payload);
      toast.success('Creado correctamente');
      setFormData({});
      setShowForm(false);
      if (activeTab === 'sellers') loadSellers();
      if (activeTab === 'buyers') loadBuyers();
      if (activeTab === 'properties') loadProperties();
      if (activeTab === 'professionals') loadProfessionals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      console.error('CRM create error:', error.response?.status, detail, error);
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' | '));
      } else {
        toast.error(detail || 'Error al crear');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openForm = () => {
    setFormData({});
    setShowForm(true);
  };

  return (
    <ProveedorTelefono orgId={selectedOrg?.org_id}>
    <motion.div initial="hidden" animate="show" variants={pageEnter}
      className="min-h-screen bg-[#050508] p-4 md:p-8">
      <div className="max-w-7xl mx-auto pt-20 md:pt-8 text-center md:text-left">
        <PageTitle>CRM — Organización Profesional</PageTitle>

        {/* Campana de notificaciones */}
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => setShowNotifPanel(v => !v)}
            className="relative p-3 rounded-xl bg-gray-800 hover:bg-gray-700 text-white transition border-2 border-white/10 shadow-lg"
            title="Notificaciones de organización"
          >
            <Bell size={22} />
            {pendingInvites.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-gray-950 animate-pulse">
                {pendingInvites.length}
              </span>
            )}
          </button>

          {showNotifPanel && (
            <div className="absolute right-0 top-14 rounded-xl shadow-2xl z-50 p-4" style={{width: '320px', maxWidth: 'none', background: '#0d0d14', border: '1px solid rgba(201,168,76,0.3)'}}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Bell size={16} style={{ color: '#c9a84c' }} />
                  Notificaciones
                </h3>
                <button onClick={() => setShowNotifPanel(false)} className="text-gray-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>

              {pendingInvites.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">Sin notificaciones pendientes</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#c9a84c' }}>Invitaciones pendientes</p>
                  {pendingInvites.map(inv => (
                    <div key={inv.organization_id} className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                      <p className="text-white font-semibold text-sm mb-1">{inv.name}</p>
                      {inv.description && <p className="text-gray-400 text-xs mb-2">{inv.description}</p>}
                      <div className="flex gap-2">
                        <button
                          onClick={() => { handleAcceptInvite(inv.organization_id); setShowNotifPanel(false); }}
                          className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded flex items-center justify-center gap-1 transition"
                        >
                          <Check size={12} /> Aceptar
                        </button>
                        <button
                          onClick={() => { handleDeclineInvite(inv.organization_id); }}
                          className="flex-1 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded flex items-center justify-center gap-1 transition"
                        >
                          <XCircle size={12} /> Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ORGANIZATIONS TAB */}
        {activeTab === 'organizations' && (
          <div className="space-y-6">
            {/* Botón crear organización */}
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => { setFormData({}); setShowForm(true); }}
              className="bg-[#c9a84c] hover:bg-[#e8c870] text-black font-display font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 text-sm transition-all duration-200 hover:shadow-[0_0_24px_rgba(201,168,76,0.4)]">
              <Plus size={16} /> Nueva organización
            </motion.button>

            {/* Modal crear organización */}
            {showForm && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="rounded-2xl p-8 max-w-lg w-full" style={{ background: '#0d0d14', border: '1px solid rgba(201,168,76,0.3)' }}>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Nueva Organización</h2>
                    <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Nombre de la organización"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full rounded-lg p-3 text-sm outline-none"
                      style={{ background: '#050508', color: '#e8e6e0', border: '1px solid rgba(201,168,76,0.25)' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.6)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.25)'}
                    />
                    <textarea
                      placeholder="Descripción (opcional)"
                      value={formData.description || ''}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full rounded-lg p-3 text-sm outline-none h-24 resize-none"
                      style={{ background: '#050508', color: '#e8e6e0', border: '1px solid rgba(201,168,76,0.25)' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.6)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.25)'}
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={handleCreateOrg}
                        className="flex-1 py-3 rounded-lg font-bold text-sm text-black transition-all"
                        style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
                      >
                        CREAR
                      </button>
                      <button
                        onClick={() => setShowForm(false)}
                        className="flex-1 bg-gray-700 text-white py-3 font-bold hover:bg-gray-600"
                      >
                        CANCELAR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 📬 INVITACIONES PENDIENTES - Solo se muestra si hay alguna */}
            {pendingInvites.length > 0 && (
              <div className="rounded-xl p-4 md:p-6" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.3)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Mail size={20} style={{ color: '#c9a84c' }} />
                  <h3 className="text-base font-bold" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>
                    Invitaciones pendientes
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full text-black font-bold" style={{ background: '#c9a84c' }}>{pendingInvites.length}</span>
                  </h3>
                </div>
                <div className="space-y-3">
                  {pendingInvites.map(inv => (
                    <div
                      key={inv.organization_id}
                      data-testid={`invite-card-${inv.organization_id}`}
                      className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-xl"
                      style={{ background: '#0d0d14', border: '1px solid rgba(201,168,76,0.2)' }}
                    >
                      <div className="flex-1">
                        <p className="text-white font-bold text-lg">{inv.name}</p>
                        {inv.description && <p className="text-gray-400 text-sm">{inv.description}</p>}
                        <p className="text-gray-500 text-xs mt-1">Te han invitado a unirte como miembro</p>
                      </div>
                      <div className="flex gap-2 w-full md:w-auto">
                        <button
                          data-testid={`accept-invite-${inv.organization_id}`}
                          onClick={() => handleAcceptInvite(inv.organization_id)}
                          className="flex-1 md:flex-initial px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-bold flex items-center justify-center gap-2 rounded-lg transition"
                        >
                          <Check size={16} /> Aceptar
                        </button>
                        <button
                          data-testid={`decline-invite-${inv.organization_id}`}
                          onClick={() => handleDeclineInvite(inv.organization_id)}
                          className="flex-1 md:flex-initial px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold flex items-center justify-center gap-2 rounded-lg transition"
                        >
                          <XCircle size={16} /> Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lista de organizaciones */}
            {loading ? (
              <div className="py-16 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : organizations.length === 0 ? (
              <motion.div variants={fadeUp}
                className="bg-[#0d0d14] border border-white/7 rounded-2xl p-14 text-center">
                <Building size={40} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/40 text-base">No tienes organizaciones todavía</p>
              </motion.div>
            ) : (
              <motion.div variants={stagger} initial="hidden" animate="show" className="grid gap-3">
                {organizations.map(org => (
                  <motion.div variants={fadeUp}
                    key={org.org_id}
                    className="group rounded-2xl p-5 transition-all duration-300 relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #1a1a24 0%, #131318 100%)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                    }}
                    whileHover={{ borderColor: 'rgba(201,168,76,0.35)', boxShadow: '0 0 0 1px rgba(201,168,76,0.15), inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.5)' }}
                  >
                    {/* Gold left accent line */}
                    <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full" style={{ background: 'rgba(201,168,76,0.5)' }} />

                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 pl-3">
                      <div className="flex items-start gap-4 flex-1">
                        {org.picture ? (
                          <img src={org.picture} alt={org.name} className="rounded-xl object-cover flex-shrink-0" style={{ width: 48, height: 48, minWidth: 48, border: '1px solid rgba(201,168,76,0.4)' }} />
                        ) : (
                          <div className="rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-lg" style={{ width: 48, height: 48, minWidth: 48, background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
                            {(org.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-bold text-base" style={{ color: '#f0ede6', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>{org.name}</h3>
                            {org.role === 'owner' && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
                                style={{ background: 'rgba(201,168,76,0.18)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)' }}>
                                <Crown size={10} /> Owner
                              </span>
                            )}
                            {org.role === 'admin' && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                style={{ background: 'rgba(0,212,200,0.12)', color: '#00d4c8', border: '1px solid rgba(0,212,200,0.3)' }}>Admin</span>
                            )}
                            {org.role === 'member' && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)' }}>Miembro</span>
                            )}
                          </div>
                          {org.description && <p className="text-xs mb-1 truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{org.description}</p>}
                          <p className="text-xs flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            <Users size={11} /> {org.member_count} miembro(s)
                          </p>
                        </div>
                      </div>

                      {/* Botones acción */}
                      <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        {[
                          { label: 'CRM', icon: <Users size={13}/>, accent: 'rgba(96,165,250,0.8)', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', show: true, action: () => { setSelectedOrg(org); setActiveTab('sellers'); } },
                          { label: 'Organigrama', icon: <Network size={13}/>, accent: 'rgba(52,211,153,0.9)', bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.3)', show: true, action: () => { setSelectedOrg(org); setActiveTab('organigrama'); } },
                          { label: 'Gestionar', icon: <UserPlus size={13}/>, accent: 'rgba(167,139,250,0.9)', bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.3)', show: ['owner','admin','lider'].includes(org.role), action: () => { setSelectedOrg(org); setActiveTab('manage-users'); } },
                          { label: 'Licencias', icon: <Crown size={13}/>, accent: '#c9a84c', bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.3)', show: ['owner','admin'].includes(org.role), action: () => { setSelectedOrg(org); setActiveTab('licenses'); } },
                          { label: 'Reglas', icon: <Zap size={13}/>, accent: 'rgba(251,191,36,0.9)', bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', show: ['owner','admin'].includes(org.role), action: () => { setSelectedOrg(org); setActiveTab('reglas'); } },
                          { label: 'Stats', icon: <BarChart2 size={13}/>, accent: '#c9a84c', bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.3)', show: ['owner','admin','lider'].includes(org.role), action: () => { setSelectedOrg(org); setActiveTab('estadisticas'); setStatsPeriod('all'); setExpandedAgent(null); loadStats(org,'all'); } },
                        ].filter(b => b.show).map(b => (
                          <motion.button key={b.label} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            onClick={b.action}
                            className="px-3 py-1.5 font-medium flex items-center justify-center gap-1.5 rounded-lg text-xs transition-all duration-200 w-full md:w-auto"
                            style={{ background: b.bg, color: b.accent, border: `1px solid ${b.border}`, fontFamily: 'var(--font-body)' }}>
                            {b.icon} {b.label}
                          </motion.button>
                        ))}

                        {/* Foto — owner o admin */}
                        {['owner','admin'].includes(org.role) && (
                          <label className="px-3 py-1.5 border text-white/70 hover:border-white/20 hover:text-white font-body font-medium flex items-center justify-center gap-1.5 rounded-lg text-xs transition-all duration-200 w-full md:w-auto cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}>
                            <Camera size={13} /> {uploadingOrgPic ? '...' : 'Foto'}
                            <input type="file" accept="image/*" className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0]; if (!file) return;
                                setUploadingOrgPic(true);
                                const fd = new FormData(); fd.append('file', file);
                                try {
                                  await axios.post(`${API}/organizations/${org.org_id}/picture`, fd);
                                  toast.success('Foto actualizada'); loadOrganizations();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir foto'); }
                                setUploadingOrgPic(false);
                              }} />
                          </label>
                        )}
                        {/* Eliminar foto — owner o admin con foto */}
                        {['owner','admin'].includes(org.role) && org.picture && (
                          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/organizations/${org.org_id}/picture`);
                                toast.success('Foto eliminada'); loadOrganizations();
                              } catch { toast.error('Error al eliminar foto'); }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 w-full md:w-auto transition-all duration-200"
                            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: 'rgba(252,165,165,0.9)' }}
                            title="Eliminar foto">
                            <X size={13} /> Foto
                          </motion.button>
                        )}
                        {/* Borrar — owner o admin */}
                        {['owner','admin'].includes(org.role) && (
                          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                            onClick={() => handleDeleteOrg(org.org_id)}
                            className="px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 hover:border-red-500/50 font-body font-medium flex items-center justify-center rounded-lg text-xs transition-all duration-200 w-full md:w-auto">
                            <Trash2 size={13} />
                          </motion.button>
                        )}
                        {/* Salir — lider o agente (owner/admin usan Borrar) */}
                        {!['owner','admin'].includes(org.role) && (
                          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                            onClick={() => handleLeaveOrg(org.org_id)}
                            className="px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:border-orange-500/50 font-body font-medium flex items-center justify-center gap-1 rounded-lg text-xs transition-all duration-200 w-full md:w-auto">
                            <LogOut size={13} /> Salir
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        )}

        {/* ORGANIGRAMA TAB */}
        {activeTab === 'organigrama' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <PageTitle className="text-2xl md:text-3xl mb-0">Organigrama E-Home</PageTitle>
              </div>
              <button
                onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border"
              >
                ← Volver
              </button>
            </div>
            <Organigrama orgId={selectedOrg.org_id} />
          </div>
        )}

        {/* OPERACIONES COMPLETADAS DASHBOARD TAB */}
        {activeTab === 'operaciones' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">
            <div className="flex justify-between items-center mb-6">
              <PageTitle className="text-2xl md:text-3xl mb-0">Operaciones Completadas E-Home</PageTitle>
              <button
                onClick={() => setActiveTab('sellers')}
                className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border"
              >
                ← Volver al CRM
              </button>
            </div>
            <VentasDashboard orgId={selectedOrg.org_id} />
          </div>
        )}

        {/* MANAGE USERS TAB */}
        {activeTab === 'manage-users' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">
            <div className="mb-6">
              <button
                onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border mb-4"
              >
                ← Volver
              </button>
            </div>
            <ManageUsers orgId={selectedOrg.org_id} orgRole={selectedOrg.role} />
          </div>
        )}

        {/* LICENSES TAB */}
        {activeTab === 'licenses' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">
            <div className="mb-6">
              <button
                onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border mb-4"
              >
                ← Volver
              </button>
            </div>
            <Licenses orgId={selectedOrg.org_id} />
          </div>
        )}

        {/* REGLAS TAB */}
        {activeTab === 'reglas' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-white">Personaliza tu Organización</h2>
              <button
                onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border"
              >
                ← Volver
              </button>
            </div>
            <OrgRules orgId={selectedOrg.org_id} orgRole={selectedOrg.role} />
          </div>
        )}

        {/* ESTADÍSTICAS TAB */}
        {activeTab === 'estadisticas' && selectedOrg && (
          <div className="rounded-2xl p-4 md:p-6">

            {/* Si hay agente seleccionado → sub-página del agente */}
            {expandedAgent && orgStats ? (() => {
              const agent = orgStats.agent_stats?.find(a => a.user_id === expandedAgent);
              if (!agent) return null;
              const aObj = orgStats.agent_objectives?.[agent.user_id] || {};
              const rings = [
                { label: 'Vendedores',   val: agent.sellers,   obj: aObj.sellers,   color: '#8b5cf6', track: '#3b0764' },
                { label: 'Compradores',  val: agent.buyers,    obj: aObj.buyers,    color: '#f59e0b', track: '#451a03' },
                { label: 'Cierres',      val: agent.completed, obj: aObj.closings,  color: '#10b981', track: '#052e16' },
                { label: 'Comisión€ firmada', val: agent.exclusivity || 0, obj: aObj.exclusivity, color: '#ec4899', track: '#500724' },
                { label: 'Total contactos', val: agent.total, obj: aObj.contacts, color: '#06b6d4', track: '#083344' },
              ];
              return (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setExpandedAgent(null)}
                      className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border rounded-lg text-sm">
                      ← Volver a estadísticas
                    </button>
                    <div className="flex items-center gap-3">
                      {agent.picture ? (
                        <img src={agent.picture} alt={agent.name} className="w-12 h-12 rounded-full object-cover border-2 border-teal-500" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-teal-800 border-2 border-teal-500 flex items-center justify-center text-white font-black text-lg">
                          {(agent.name || 'A')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-black text-white">{agent.name}</h2>
                        <p className="text-teal-400 text-sm font-semibold">{agent.closure_rate}% tasa de cierre</p>
                      </div>
                    </div>
                  </div>

                  {/* Anillos de progreso */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                    {rings.map(ring => {
                      const maxV = (ring.obj > 0) ? ring.obj : Math.max(ring.val, 1);
                      const pct = Math.min(ring.val / maxV, 1);
                      const r = 46, cx = 56, cy = 56, circ = 2 * Math.PI * r;
                      const dash = circ * pct;
                      return (
                        <div key={ring.label} className="bg-gray-800 rounded-2xl p-4 flex flex-col items-center border border-white/10">
                          <svg width="112" height="112" viewBox="0 0 112 112">
                            <circle cx={cx} cy={cy} r={r} fill="none" stroke={ring.track} strokeWidth="12" />
                            <circle cx={cx} cy={cy} r={r} fill="none" stroke={ring.color} strokeWidth="12"
                              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                              transform={`rotate(-90 ${cx} ${cy})`}
                              style={{ transition: 'stroke-dasharray 0.8s ease' }} />
                            <text x={cx} y={cy - 8} textAnchor="middle" fill="white" fontSize="22" fontWeight="bold">{ring.val}</text>
                            {ring.obj > 0 && (
                              <text x={cx} y={cy + 12} textAnchor="middle" fill="#9ca3af" fontSize="11">/{ring.obj}</text>
                            )}
                          </svg>
                          <p className="text-white font-bold text-xs text-center mt-2">{ring.label}</p>
                          {ring.obj > 0 && (
                            <p className="text-xs mt-0.5 font-semibold" style={{ color: ring.color }}>
                              {Math.round(pct * 100)}% del objetivo
                            </p>
                          )}
                          {ring.obj <= 0 && (
                            <p className="text-gray-500 text-xs mt-0.5">sin objetivo fijado</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Barra cierre */}
                  <div className="bg-gray-800 rounded-xl p-5 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-bold text-sm">Tasa de cierre personal</span>
                      <span className="text-teal-400 font-black">{agent.closure_rate}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                      <div className="h-4 rounded-full bg-gradient-to-r from-teal-500 to-green-400 transition-all duration-700"
                        style={{ width: `${Math.min(agent.closure_rate, 100)}%` }} />
                    </div>
                    <p className="text-gray-400 text-xs mt-2">{agent.completed} operaciones cerradas de {agent.total} contactos totales</p>
                  </div>
                </div>
              );
            })() : (
              <>
                {/* ── CABECERA + FILTRO PERÍODO ───────────────── */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <h2 className="text-xl font-black text-white flex items-center gap-2">
                    <BarChart2 className="text-teal-400" size={24} /> Estadísticas — {selectedOrg.name}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[['all','Todo'],['month','30 días'],['week','7 días'],['day','Hoy']].map(([p, label]) => (
                      <button key={p}
                        onClick={() => { setStatsPeriod(p); loadStats(selectedOrg, p); }}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition ${
                          statsPeriod === p
                            ? 'bg-teal-500 border-teal-400 text-white'
                            : 'bg-gray-800 border-white/10 text-gray-300 hover:border-teal-500'
                        }`}>
                        {label}
                      </button>
                    ))}
                    <button onClick={() => setActiveTab('organizations')}
                      className="px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-75 rounded-lg border rounded-lg text-sm">
                      ← Volver
                    </button>
                  </div>
                </div>

                {loadingStats ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-gray-400">Calculando estadísticas...</p>
                    </div>
                  </div>
                ) : orgStats ? (
                  <>
                    {/* KPIs globales */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {[
                        { label: 'Total contactos', value: orgStats.total_contacts, color: 'from-blue-600 to-cyan-600', obj: orgStats.objectives?.contacts },
                        { label: 'Operaciones cerradas', value: orgStats.completed_operations, color: 'from-green-600 to-emerald-600', obj: orgStats.objectives?.closings },
                        { label: 'Tasa de cierre', value: `${orgStats.closure_rate}%`, color: 'from-amber-600 to-yellow-500', obj: null },
                        { label: 'Miembros activos', value: orgStats.total_members, color: 'from-yellow-600 to-orange-600', obj: null },
                      ].map(k => (
                        <div key={k.label} className={`bg-gradient-to-br ${k.color} p-5 rounded-xl`}>
                          <div className="text-white/70 text-xs font-medium mb-1">{k.label}</div>
                          <div className="text-white text-3xl font-black">{k.value}</div>
                          {k.obj > 0 && (
                            <div className="text-white/60 text-xs mt-1">obj: {k.obj}</div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* KPIs secundarios */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {[
                        { label: 'Vendedores', value: orgStats.total_sellers, icon: '👤', obj: orgStats.objectives?.sellers },
                        { label: 'Compradores', value: orgStats.total_buyers, icon: '🔍', obj: orgStats.objectives?.buyers },
                        { label: 'Propiedades CRM', value: orgStats.total_properties || 0, icon: '🏠', obj: null },
                        { label: 'Profesionales', value: orgStats.total_professionals || 0, icon: '💼', obj: null },
                      ].map(k => (
                        <div key={k.label} className="bg-gray-800 rounded-xl p-4 border border-white/10">
                          <div className="text-xl mb-1">{k.icon}</div>
                          <div className="text-white text-2xl font-black">{k.value}</div>
                          <div className="text-gray-400 text-xs mt-0.5">{k.label}</div>
                          {k.obj > 0 && <div className="text-gray-500 text-xs">obj: {k.obj}</div>}
                        </div>
                      ))}
                    </div>

                    {/* Barra de cierre global */}
                    <div className="bg-gray-800 rounded-xl p-5 mb-6 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-bold text-sm">Progreso global de cierre</span>
                        <span className="text-teal-400 font-black">{orgStats.closure_rate}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                        <div className="h-4 rounded-full bg-gradient-to-r from-teal-500 to-green-400 transition-all duration-700"
                          style={{ width: `${Math.min(orgStats.closure_rate, 100)}%` }} />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        {orgStats.objectives?.closings > 0 && (
                          <span className="text-teal-400">Objetivo: {orgStats.objectives.closings} cierres</span>
                        )}
                        <span>100%</span>
                      </div>
                    </div>

                    {/* Agentes */}
                    <div>
                      <h3 className="text-white font-black text-lg mb-4 flex items-center gap-2">
                        <Users size={18} style={{ color: '#c9a84c' }} /> Rendimiento por agente
                        <span className="text-gray-500 text-sm font-normal">— haz clic para ver detalle completo</span>
                      </h3>
                      {orgStats.agent_stats && orgStats.agent_stats.length > 0 ? (
                        <div className="space-y-3">
                          {[...orgStats.agent_stats]
                            .sort((a, b) => b.total - a.total)
                            .map((agent, i) => (
                              <div key={i}
                                onClick={() => setExpandedAgent(agent.user_id)}
                                className="bg-gray-800 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-700 hover:border-teal-500 border-2 border-transparent transition-all group">
                                {agent.picture ? (
                                  <img src={agent.picture} alt={agent.name} className="w-11 h-11 rounded-full object-cover border-2 border-teal-500 flex-shrink-0" />
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-gray-600 border-2 border-teal-500 flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                                    {(agent.name || 'A')[0].toUpperCase()}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-white font-bold text-sm">{agent.name}</span>
                                    <span className="text-teal-400 font-black text-sm">{agent.closure_rate}%</span>
                                  </div>
                                  <div className="w-full bg-gray-700 rounded-full h-2.5 mb-1">
                                    <div className="h-2.5 rounded-full bg-gradient-to-r from-teal-500 to-green-400 transition-all"
                                      style={{ width: `${Math.min(agent.closure_rate, 100)}%` }} />
                                  </div>
                                  <div className="flex gap-3 text-gray-400 text-xs">
                                    <span>👤 {agent.sellers} vend.</span>
                                    <span>🔍 {agent.buyers} comp.</span>
                                    <span>✅ {agent.completed}/{agent.total} cerrados</span>
                                  </div>
                                </div>
                                <span className="text-gray-500 text-xs group-hover:text-teal-400 transition ml-2 flex-shrink-0">Ver →</span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="bg-gray-800 rounded-xl p-10 text-center border border-white/10">
                          <Users size={40} className="mx-auto text-gray-600 mb-3" />
                          <p className="text-gray-400">Sin datos de agentes todavía</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-gray-400 text-center py-12">No hay estadísticas disponibles</p>
                )}

                {/* ── ESTABLECER OBJETIVOS ─────────────────── */}
                <ObjectivesForm orgId={selectedOrg?.org_id} currentPeriod={statsPeriod} />
              </>
            )}
          </div>
        )}

        {/* IDEALISTA — solo E-Home */}
        {activeTab === 'idealista' && selectedOrg?.org_id === ORG_CON_CAPTACION && (
          <div className="rounded-2xl">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-black text-white">Portales</h2>
                <p className="text-white/40 text-xs mt-0.5">Propietarios particulares de Fotocasa y pisos.com, con su teléfono.</p>
              </div>
              <button onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold text-white/60 hover:text-white rounded-lg border border-white/10">← Volver</button>
            </div>
            <SeccionIdealista orgId={selectedOrg.org_id} />
          </div>
        )}

        {/* LLAMADAS — solo E-Home */}
        {activeTab === 'llamadas' && selectedOrg?.org_id === ORG_CON_CAPTACION && (
          <div className="rounded-2xl">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-black text-white">Llamadas</h2>
                <p className="text-white/40 text-xs mt-0.5">Quién ha autorizado que le llamemos, y qué salió de cada llamada.</p>
              </div>
              <button onClick={() => setActiveTab('organizations')}
                className="px-4 py-2 text-sm font-semibold text-white/60 hover:text-white rounded-lg border border-white/10">← Volver</button>
            </div>
            <SeccionLlamadas orgId={selectedOrg.org_id} />
          </div>
        )}

        {activeTab === 'mail' && selectedOrg?.org_id === ORG_CON_CAPTACION && (
          <motion.div variants={pageEnter} initial="hidden" animate="show">
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 19, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 9 }}>
                <Mail size={19} style={{ color: '#c9a84c' }} /> Correo
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                Buzón de info@ehomesp.com. Lo que hagas aquí se refleja en el móvil y en el webmail.
              </p>
            </div>
            <SeccionMail orgId={selectedOrg.org_id} />
          </motion.div>
        )}

        {activeTab === 'whatsapp' && selectedOrg?.org_id === ORG_CON_CAPTACION && (
          <motion.div variants={pageEnter} initial="hidden" animate="show">
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 19, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 9 }}>
                <MessageCircle size={19} style={{ color: '#25D366' }} /> WhatsApp
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                Una sola sesión para toda la organización. Sin bot: contesta una persona.
              </p>
            </div>
            <SeccionWhatsApp orgId={selectedOrg.org_id} abrirNumero={chatAAbrir}
                             onAbierto={() => setChatAAbrir(null)} />
          </motion.div>
        )}

        {/* CALENDARIO — para cualquier organizacion, no solo eHome. La fecha de
            visita de una ficha aparece aqui sola: no hay que apuntarla dos veces. */}
        {activeTab === 'calendario' && selectedOrg && (
          <motion.div variants={pageEnter} initial="hidden" animate="show">
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 19, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 9 }}>
                <CalendarDays size={19} style={{ color: '#c9a84c' }} /> Calendario
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                Visitas y reuniones con vendedores, compradores y profesionales.
                Lo que agendes en una ficha aparece aqui solo.
              </p>
            </div>
            <Calendario orgId={selectedOrg.org_id} />
          </motion.div>
        )}

        {/* OTHER TABS - Original Content */}
        {activeTab !== 'organizations' && activeTab !== 'organigrama' && activeTab !== 'manage-users' && activeTab !== 'licenses' && activeTab !== 'operaciones' && activeTab !== 'reglas' && activeTab !== 'estadisticas' && activeTab !== 'idealista' && activeTab !== 'llamadas' && (
          <>
            {/* Tab Buttons — nuevo estilo minimal */}
            <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white/4 border border-white/8 rounded-xl w-fit">
              {[
                { id: 'sellers',     label: 'Vendedores',   count: sellers.length,     icon: <Users size={13}/> },
                { id: 'buyers',      label: 'Compradores',  count: buyers.length,      icon: <Phone size={13}/> },
                { id: 'properties',  label: 'Propiedades',  count: properties.length,  icon: <Building size={13}/> },
                { id: 'professionals',label:'Profesionales', count: professionals.length,icon: <Building size={13}/> },
                { id: 'operaciones', label: 'Operaciones',  count: null,               icon: <CheckCircle size={13}/> },
                { id: 'calendario',  label: 'Calendario',   count: null,               icon: <CalendarDays size={13}/> },
                ...(selectedOrg?.org_id === ORG_CON_CAPTACION ? [
                  { id: 'idealista',  label: 'Portales', count: null, icon: <Zap size={13}/> },
                  { id: 'llamadas',   label: 'Llamadas',  count: null, icon: <Phone size={13}/> },
                  { id: 'mail',       label: 'Correo',    count: null, icon: <Mail size={13}/> },
                  { id: 'whatsapp',   label: 'WhatsApp',  count: null, icon: <MessageCircle size={13}/> },
                ] : []),
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-body font-semibold transition-all duration-200 ${
                    activeTab === t.id
                      ? 'bg-[#c9a84c] text-black shadow-[0_0_16px_rgba(201,168,76,0.3)]'
                      : 'text-white/50 hover:text-white'
                  }`}>
                  {t.icon}
                  {t.label}
                  {t.count !== null && (
                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      activeTab === t.id ? 'bg-black/20 text-black' : 'bg-white/10 text-white/40'
                    }`}>{t.count}</span>
                  )}
                </button>
              ))}
            </div>

            {selectedOrg && (
              <div className="mb-5 bg-[#0d0d14] border border-white/7 rounded-xl p-3 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                <p className="text-white/70 text-sm font-body flex items-center gap-2">
                  <Building size={14} className="text-[#c9a84c]" />
                  <span className="text-[#c9a84c] font-semibold">{selectedOrg.name}</span>
                </p>
                <div className="flex gap-2 w-full md:w-auto">
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    data-testid="open-team-chat-btn" onClick={() => setShowTeamChat(true)}
                    className="bg-white/6 border border-white/10 text-white/70 hover:text-white hover:border-white/20 px-3 py-1.5 font-body font-medium flex items-center gap-2 rounded-lg text-xs transition-all flex-1 md:flex-initial justify-center">
                    <MessageCircle size={13} /> Chat equipo
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={() => { setActiveTab('organizations'); setSelectedOrg(null); }}
                    className="bg-white/6 border border-white/10 text-white/70 hover:text-white hover:border-white/20 px-3 py-1.5 font-body font-medium flex items-center gap-1.5 rounded-lg text-xs transition-all flex-1 md:flex-initial justify-center">
                    ← Volver
                  </motion.button>
                </div>
              </div>
            )}
            
            {showTeamChat && selectedOrg && (
              <TeamChatModal
                orgId={selectedOrg.org_id}
                orgName={selectedOrg.name}
                currentUserId={user?.user_id}
                currentUserRole={selectedOrg.role}
                onClose={() => setShowTeamChat(false)}
              />
            )}
            
            {/* Inline accordion form */}
            <div className="mb-5">
              <div
                onClick={() => setShowForm(!showForm)}
                className="flex items-center justify-between p-3.5 rounded-xl cursor-pointer transition-all mb-1"
                style={{ background: showForm ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.07)', border: `1px solid ${showForm ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.2)'}` }}>
                <div className="flex items-center gap-2">
                  <Plus size={14} style={{ color: '#c9a84c' }} />
                  <span className="text-sm font-bold" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
                    Nuevo {activeTab === 'sellers' ? 'Vendedor' : activeTab === 'buyers' ? 'Comprador' : activeTab === 'properties' ? 'Propiedad' : 'Profesional'}
                  </span>
                </div>
                <span style={{ color: '#c9a84c', fontSize: '0.7rem', transition: 'transform 0.2s', transform: showForm ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
              </div>
              {showForm && (
                <div className="rounded-xl p-5 space-y-3 animate-in" style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {activeTab === 'sellers' && (<>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="*Nombre" value={formData.nombre || ''} onChange={e => setFormData({...formData, nombre: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="tel" placeholder="Teléfono" value={formData.telefono || ''} onChange={e => setFormData({...formData, telefono: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <input type="email" placeholder="Email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Zona" value={formData.zona || ''} onChange={e => setFormData({...formData, zona: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <select value={formData.tipo_vivienda || ''} onChange={e => setFormData({...formData, tipo_vivienda: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm">
                        <option value="">Tipo Vivienda</option>
                        <option value="piso">Piso</option><option value="casa">Casa</option><option value="chalet">Chalet</option><option value="duplex">Dúplex</option><option value="atico">Ático</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="number" placeholder="Precio Estimado (€)" value={formData.precio_estimado || ''} onChange={e => setFormData({...formData, precio_estimado: parseFloat(e.target.value) || ''})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="number" placeholder="Comisión (%)" value={formData.comision_ofertada || ''} onChange={e => setFormData({...formData, comision_ofertada: parseFloat(e.target.value) || ''})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <select value={formData.motivacion || 'media'} onChange={e => setFormData({...formData, motivacion: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm">
                        <option value="alta">🔴 Motivación ALTA</option><option value="media">🟡 Motivación MEDIA</option><option value="baja">🟢 Motivación BAJA</option>
                      </select>
                      <select value={formData.estado || 'nuevo'} onChange={e => setFormData({...formData, estado: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm">
                        <option value="nuevo">🆕 Nuevo</option><option value="contactado">📞 Contactado</option><option value="interesado">⭐ Interesado</option><option value="visita_agendada">📅 Visita Agendada</option><option value="exclusiva_firmada">✍️ Exclusiva Firmada</option><option value="descartado">❌ Descartado</option>
                      </select>
                    </div>
                    <textarea placeholder="Notas..." value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm h-20" />
                  </>)}
                  {activeTab === 'buyers' && (<>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="*Nombre" value={formData.nombre || ''} onChange={e => setFormData({...formData, nombre: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="tel" placeholder="Teléfono" value={formData.telefono || ''} onChange={e => setFormData({...formData, telefono: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <input type="email" placeholder="Email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="number" placeholder="Presupuesto (€)" value={formData.presupuesto || ''} onChange={e => setFormData({...formData, presupuesto: parseFloat(e.target.value) || ''})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="text" placeholder="Zona Preferida" value={formData.zona || ''} onChange={e => setFormData({...formData, zona: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <select value={formData.urgencia || 'media'} onChange={e => setFormData({...formData, urgencia: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm">
                        <option value="alta">🔴 Urgencia ALTA</option><option value="media">🟡 Urgencia MEDIA</option><option value="baja">🟢 Urgencia BAJA</option>
                      </select>
                      <select value={formData.estado || 'nuevo'} onChange={e => setFormData({...formData, estado: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm">
                        <option value="nuevo">🆕 Nuevo</option><option value="contactado">📞 Contactado</option><option value="interesado">⭐ Interesado</option><option value="descartado">❌ Descartado</option>
                      </select>
                    </div>
                    <textarea placeholder="Notas..." value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm h-20" />
                  </>)}
                  {activeTab === 'properties' && (<>
                    <input type="text" placeholder="Dirección" value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Nombre Propietario" value={formData.owner_name || ''} onChange={e => setFormData({...formData, owner_name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="text" placeholder="Contacto Propietario" value={formData.owner_contact || ''} onChange={e => setFormData({...formData, owner_contact: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Tipo de Propiedad" value={formData.property_type || ''} onChange={e => setFormData({...formData, property_type: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="number" placeholder="Precio Estimado (€)" value={formData.estimated_price || ''} onChange={e => setFormData({...formData, estimated_price: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <textarea placeholder="Notas..." value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm h-20" />
                  </>)}
                  {activeTab === 'professionals' && (<>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Nombre Comercial" value={formData.nombre_comercial || ''} onChange={e => setFormData({...formData, nombre_comercial: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="text" placeholder="Responsable" value={formData.responsable || ''} onChange={e => setFormData({...formData, responsable: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="email" placeholder="Email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                      <input type="tel" placeholder="Teléfono" value={formData.telefono || ''} onChange={e => setFormData({...formData, telefono: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    </div>
                    <input type="text" placeholder="Domicilio" value={formData.domicilio || ''} onChange={e => setFormData({...formData, domicilio: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" />
                    <textarea placeholder="Notas..." value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm h-20" />
                  </>)}
                  <div className="flex gap-3 pt-2">
                    <button onClick={handleCreate} disabled={submitting}
                      className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', color: '#000' }}
                      onMouseEnter={e => !submitting && (e.currentTarget.style.opacity = '0.9')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                      {submitting ? 'Creando...' : 'Crear'}
                    </button>
                    <button onClick={() => { setShowForm(false); setFormData({}); }}
                      className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div className="py-12 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <motion.div variants={stagger} initial="hidden" animate="show" className="grid gap-3">
                {getCurrentData().map(item => (
                  <motion.div key={item[getIdField()]} variants={fadeUp}>
                  <ItemCard
                    key={item[getIdField()]}
                    item={item}
                    activeTab={activeTab}
                    getStatusColor={getStatusColor}
                    onDelete={() => handleDelete(item[getIdField()], activeTab,
                      item.nombre || item.name || item.direccion || item.address)}
                    onUploadDoc={(file) => handleUploadDocument(item[getIdField()], file)}
                    onDeleteDoc={(docUrl) => handleDeleteDocument(item[getIdField()], docUrl)}
                    uploadingDoc={uploadingDoc}
                  />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}

        {/* Modals */}
        {showForm && activeTab !== 'organizations' && (
          <FormModal
            activeTab={activeTab}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            onClose={() => setShowForm(false)}
          />
        )}

        {selectedContact && (
          <ContactDetailModal
            isOpen={!!selectedContact}
            orgId={selectedOrg?.org_id}
            type={activeTab === 'buyers' ? 'buyer' : 'seller'}
            contact={selectedContact}
            onClose={() => setSelectedContact(null)}
            onSave={async (actualizado) => {
              try {
                const ruta = getBackendPath(activeTab);
                const id = actualizado.seller_id || actualizado.buyer_id;
                await axios.put(`${API}/${ruta}/${id}`, actualizado);
                toast.success('Ficha guardada');
              } catch (e) {
                toast.error('No se pudo guardar la ficha');
              }
              setSelectedContact(null);
              if (activeTab === 'sellers') loadSellers();
              if (activeTab === 'buyers') loadBuyers();
            }}
          />
        )}
      </div>
    </motion.div>
    </ProveedorTelefono>
  );
};

const ObjectivesForm = ({ orgId, currentPeriod }) => {
  const [open, setOpen] = React.useState(false);
  const [period, setPeriod] = React.useState(currentPeriod || 'all');
  const [vals, setVals] = React.useState({ sellers: '', buyers: '', closings: '', contacts: '' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setPeriod(currentPeriod || 'all'); }, [currentPeriod]);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      const key = period === 'all' ? 'objectives' : `objectives_${period}`;
      await axios.put(`${API}/organizations/${orgId}/settings`, {
        [key]: {
          sellers: parseFloat(vals.sellers) || 0,
          buyers: parseFloat(vals.buyers) || 0,
          closings: parseFloat(vals.closings) || 0,
          contacts: parseFloat(vals.contacts) || 0,
        }
      });
      toast.success('Objetivos guardados');
      setOpen(false);
    } catch {
      toast.error('Error al guardar objetivos');
    }
    setSaving(false);
  };

  const periodLabels = { all: 'Total', month: 'Mensual', week: 'Semanal', day: 'Diario' };

  return (
    <div className="mt-6">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between p-3.5 rounded-xl cursor-pointer transition-all"
        style={{ background: open ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${open ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
        <span className="text-sm font-semibold" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
          Establecer Objetivos
        </span>
        <span style={{ color: '#c9a84c', fontSize: '0.65rem', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
      </div>
      {open && (
        <div className="mt-1 p-5 rounded-xl space-y-4" style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Selector de periodo */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-2.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Periodo</p>
            <div className="flex gap-2 flex-wrap">
              {[['all','Total'],['month','Mensual'],['week','Semanal'],['day','Diario']].map(([p, lbl]) => (
                <button key={p} onClick={() => setPeriod(p)}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: period === p ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.04)',
                    color: period === p ? '#c9a84c' : 'rgba(255,255,255,0.4)',
                    border: `1px solid ${period === p ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {/* Campos */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-2.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Objetivos {periodLabels[period]}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[['sellers','Vendedores'],['buyers','Compradores'],['closings','Cierres'],['contacts','Total contactos']].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-[10px] text-white/40 mb-1 block">{lbl}</label>
                  <input type="number" min="0" placeholder="0" value={vals[k]}
                    onChange={e => setVals({...vals, [k]: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg text-sm" />
                </div>
              ))}
            </div>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', color: '#000' }}>
            {saving ? 'Guardando...' : `Guardar objetivos ${periodLabels[period]}`}
          </button>
        </div>
      )}
    </div>
  );
};

const ItemCard = ({ item, activeTab, getStatusColor, onDelete, onUploadDoc, onDeleteDoc, uploadingDoc, onUpdated }) => {
  const [editing, setEditing] = React.useState(false);
  const [editData, setEditData] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [localItem, setLocalItem] = React.useState(item);

  React.useEffect(() => { setLocalItem(item); }, [item]);

  const idField = {
    sellers: 'seller_id',
    buyers: 'buyer_id',
    properties: 'captured_id',
    professionals: 'professional_id'
  }[activeTab];

  const endpointBase = {
    sellers: 'crm/sellers',
    buyers: 'crm/buyers',
    properties: 'crm/properties',
    professionals: 'crm/professionals'
  }[activeTab];

  const displayName = localItem.nombre || localItem.name || localItem.nombre_comercial || 'Sin nombre';
  const displayEmail = localItem.email || '';
  const displayPhone = localItem.phone || localItem.telefono || localItem.owner_contact || '';

  const EDIT_FIELDS = {
    sellers: [
      { key: 'nombre',            label: 'Nombre propietario', type: 'text' },
      { key: 'telefono',          label: 'Teléfono',           type: 'tel' },
      { key: 'email',             label: 'Email',              type: 'email' },
      { key: 'zona',              label: 'Zona / Barrio',      type: 'text' },
      { key: 'tipo_vivienda',     label: 'Tipo vivienda',      type: 'text' },
      { key: 'precio_estimado',   label: 'Precio estimado (€)',type: 'number' },
      { key: 'comision_ofertada', label: 'Comisión ofertada (%)',type: 'number' },
      { key: 'motivacion',        label: 'Motivación de venta',type: 'text' },
      { key: 'estado',            label: 'Estado',             type: 'select',
        options: ['nuevo','contactado','visita_agendada','exclusiva_firmada','cerrado','descartado'] },
      { key: 'fecha_visita',      label: 'Fecha visita',       type: 'date' },
      { key: 'notes',             label: 'Notas',              type: 'textarea' },
    ],
    buyers: [
      { key: 'nombre',            label: 'Nombre comprador',   type: 'text' },
      { key: 'telefono',          label: 'Teléfono',           type: 'tel' },
      { key: 'email',             label: 'Email',              type: 'email' },
      { key: 'zona',              label: 'Zona buscada',       type: 'text' },
      { key: 'tipo_propiedad',    label: 'Tipo propiedad',     type: 'text' },
      { key: 'presupuesto',       label: 'Presupuesto (€)',    type: 'number' },
      { key: 'urgencia',          label: 'Urgencia',           type: 'select',
        options: ['baja','media','alta'] },
      { key: 'estado',            label: 'Estado',             type: 'select',
        options: ['nuevo','contactado','visita_agendada','oferta_enviada','cerrado','descartado'] },
      { key: 'notes',             label: 'Notas',              type: 'textarea' },
    ],
    properties: [
      { key: 'direccion',         label: 'Dirección',          type: 'text' },
      { key: 'owner_name',        label: 'Propietario',        type: 'text' },
      { key: 'owner_contact',     label: 'Contacto propietario',type: 'text' },
      { key: 'property_type',     label: 'Tipo inmueble',      type: 'text' },
      { key: 'estimated_price',   label: 'Precio estimado (€)',type: 'number' },
      { key: 'notes',             label: 'Notas',              type: 'textarea' },
    ],
    professionals: [
      { key: 'nombre_comercial',  label: 'Nombre comercial',   type: 'text' },
      { key: 'responsable',       label: 'Responsable',        type: 'text' },
      { key: 'email',             label: 'Email',              type: 'email' },
      { key: 'telefono',          label: 'Teléfono',           type: 'tel' },
      { key: 'domicilio',         label: 'Domicilio',          type: 'text' },
      { key: 'notes',             label: 'Notas',              type: 'textarea' },
    ],
  };

  const openEdit = () => {
    const initial = {};
    (EDIT_FIELDS[activeTab] || []).forEach(f => {
      initial[f.key] = localItem[f.key] ?? '';
    });
    setEditData(initial);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {};
      Object.entries(editData).forEach(([k, v]) => { if (v !== undefined && v !== '') payload[k] = v; });
      const res = await axios.put(`${API}/${endpointBase}/${localItem[idField]}`, payload);
      setLocalItem(res.data);
      setEditing(false);
      toast.success('Guardado');
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/${endpointBase}/${localItem[idField]}/picture`, fd);
      setLocalItem(prev => ({ ...prev, photo_url: res.data.photo_url }));
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) onUploadDoc(file);
  };

  const urgencyColor = (u) => u === 'alta' ? { bg: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'rgba(239,68,68,0.3)' }
    : u === 'media' ? { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: 'rgba(251,191,36,0.3)' }
    : { bg: 'rgba(52,211,153,0.12)', color: '#34d399', border: 'rgba(52,211,153,0.25)' };

  return (
    <div className="rounded-xl overflow-hidden transition-all duration-200 hover:border-[rgba(201,168,76,0.3)]"
      style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.07)' }}>

      {/* Header */}
      <div className="p-4 flex items-start gap-3"
        style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, transparent 60%)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>

        {/* Avatar — clickable para cambiar foto */}
        <label className="relative w-12 h-12 rounded-xl flex-shrink-0 cursor-pointer group overflow-hidden"
          style={{ background: 'rgba(201,168,76,0.14)', border: '1px solid rgba(201,168,76,0.3)' }}>
          {localItem.photo_url
            ? <img src={localItem.photo_url} alt={displayName} className="w-full h-full object-cover" />
            : <span className="w-full h-full flex items-center justify-center text-base font-bold"
                style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
                {displayName.charAt(0).toUpperCase()}
              </span>
          }
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {uploadingPhoto
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Camera size={14} style={{ color: '#c9a84c' }} />
            }
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
        </label>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white truncate mb-1.5" style={{ fontFamily: 'var(--font-display)' }}>{displayName}</h3>
          <div className="flex gap-1.5 flex-wrap items-center">
            {(localItem.estado || localItem.status) && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}>
                {localItem.estado || localItem.status}
              </span>
            )}
            {(localItem.urgency || localItem.urgencia) && (() => {
              const u = localItem.urgency || localItem.urgencia;
              const c = urgencyColor(u);
              return <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>{u}</span>;
            })()}
            {localItem.created_by_name && (
              <span className="px-2.5 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-1"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)' }}>
                <UserPlus size={10} /> {localItem.created_by_name}
              </span>
            )}
          </div>
        </div>

        {/* Acciones header */}
        <div className="flex gap-1 flex-shrink-0 items-center">
          {/* Llamar como Marcos o como Sergio, y escribir por WhatsApp. El
              telefono puede venir en distintos campos segun el tipo de ficha. */}
          {(() => {
            const tel = localItem.telefono || localItem.phone || localItem.movil || null;
            if (!tel) return null;
            return (
              <>
                <BotonesLlamar telefono={tel} nombre={displayName} compacto
                  sellerId={activeTab === 'sellers' ? localItem.seller_id : undefined}
                  buyerId={activeTab === 'buyers' ? localItem.buyer_id : undefined} />
                <button
                  onClick={() => {
                    // Aqui NO se redacta nada: eso es cosa de la seccion de
                    // anuncios, donde el contacto es en frio. A un vendedor que
                    // ya es tuyo se le abre el chat y se le escribe a mano.
                    const n = String(tel).replace(/\D/g, '');
                    window.dispatchEvent(new CustomEvent('ehome:abrir-whatsapp', { detail: { wa_id: n } }));
                  }}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                  title="Abrir el chat de WhatsApp con esta persona"
                  onMouseEnter={e => { e.currentTarget.style.color = '#25d366'; e.currentTarget.style.background = 'rgba(37,211,102,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.background = 'transparent'; }}>
                  <MessageCircle size={14} />
                </button>
              </>
            );
          })()}
          <button onClick={openEdit} className="p-1.5 rounded-lg transition-all"
            style={{ color: 'rgba(255,255,255,0.35)' }}
            title="Editar"
            onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.background = 'transparent'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg transition-all"
            style={{ color: 'rgba(255,255,255,0.25)' }}
            title="Eliminar"
            onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.25)'; e.currentTarget.style.background = 'transparent'; }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Edit form inline */}
      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="p-4 space-y-3" style={{ background: 'rgba(201,168,76,0.04)' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#c9a84c' }}>Editar ficha</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(EDIT_FIELDS[activeTab] || []).filter(f => f.type !== 'textarea').map(f => {
                  const inputStyle = { border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' };
                  const inputClass = "w-full px-3 py-2 rounded-lg text-xs text-white";
                  if (f.type === 'select') return (
                    <div key={f.key}>
                      <label className="text-[10px] text-white/40 mb-1 block">{f.label}</label>
                      <select value={editData[f.key] || ''} onChange={e => setEditData(d => ({ ...d, [f.key]: e.target.value }))}
                        className={inputClass} style={{ ...inputStyle, color: editData[f.key] ? '#fff' : 'rgba(255,255,255,0.35)' }}>
                        <option value="">— {f.label} —</option>
                        {f.options.map(o => <option key={o} value={o} style={{ background: '#0d0d14' }}>{o}</option>)}
                      </select>
                    </div>
                  );
                  return (
                    <div key={f.key}>
                      <label className="text-[10px] text-white/40 mb-1 block">{f.label}</label>
                      <input type={f.type} placeholder={f.label}
                        value={editData[f.key] || ''}
                        onChange={e => setEditData(d => ({ ...d, [f.key]: e.target.value }))}
                        className={inputClass} style={inputStyle} />
                    </div>
                  );
                })}
              </div>
              {(EDIT_FIELDS[activeTab] || []).filter(f => f.type === 'textarea').map(f => (
                <div key={f.key}>
                  <label className="text-[10px] text-white/40 mb-1 block">{f.label}</label>
                  <textarea placeholder={f.label} rows={2}
                    value={editData[f.key] || ''}
                    onChange={e => setEditData(d => ({ ...d, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-xs text-white resize-none"
                    style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }} />
                </div>
              ))}
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} disabled={saving}
                  className="flex-1 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', color: '#000' }}>
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Cancelar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Body */}
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {displayEmail && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Mail size={13} style={{ color: '#c9a84c', flexShrink: 0 }} />
              <span className="text-xs text-white/70 truncate">{displayEmail}</span>
            </div>
          )}
          {displayPhone && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Phone size={13} style={{ color: '#c9a84c', flexShrink: 0 }} />
              <span className="text-xs text-white/70">{displayPhone}</span>
            </div>
          )}
        </div>

        {localItem.notes && (
          <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#c9a84c' }}>Notas</p>
            <p className="text-xs text-white/60 leading-relaxed">{localItem.notes}</p>
          </div>
        )}

        {localItem.documents && localItem.documents.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {localItem.documents.map((doc, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <a href={`${BACKEND_URL}${doc}`} target="_blank" rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition flex items-center gap-1">
                  <FileText size={11} /> Doc {idx + 1}
                </a>
                <button onClick={() => onDeleteDoc(doc)} className="text-white/30 hover:text-red-400 transition">
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all"
          style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.18)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.1)'}>
          <Upload size={13} />
          {uploadingDoc === localItem[idField] ? 'Subiendo...' : 'Subir Doc'}
          <input type="file" className="hidden" onChange={handleFileSelect} disabled={uploadingDoc === localItem[idField]} />
        </label>
      </div>
    </div>
  );
};

const FormModal = ({ activeTab, formData, setFormData, onSubmit, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-8 max-w-3xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Nuevo {activeTab === 'sellers' ? 'Vendedor' : activeTab === 'buyers' ? 'Comprador' : activeTab === 'properties' ? 'Propiedad' : 'Profesional'}</h2>
          <button onClick={onClose} className="text-2xl hover:text-red-500">
            <X />
          </button>
        </div>
        
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {activeTab === 'sellers' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="text" 
                  placeholder="*Nombre" 
                  value={formData.nombre || ''} 
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                  required
                />
                <input 
                  type="tel" 
                  placeholder="Teléfono" 
                  value={formData.telefono || ''} 
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                />
              </div>
              
              <input 
                type="email" 
                placeholder="Email" 
                value={formData.email || ''} 
                onChange={(e) => setFormData({...formData, email: e.target.value})} 
                className="w-full p-3 border-4 border-black" 
              />
              
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="text" 
                  placeholder="Zona" 
                  value={formData.zona || ''} 
                  onChange={(e) => setFormData({...formData, zona: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                />
                <select 
                  value={formData.tipo_vivienda || ''} 
                  onChange={(e) => setFormData({...formData, tipo_vivienda: e.target.value})} 
                  className="w-full p-3 border-4 border-black"
                >
                  <option value="">Tipo Vivienda</option>
                  <option value="piso">Piso</option>
                  <option value="casa">Casa</option>
                  <option value="chalet">Chalet</option>
                  <option value="duplex">Dúplex</option>
                  <option value="atico">Ático</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="number" 
                  placeholder="Precio Estimado (€)" 
                  value={formData.precio_estimado || ''} 
                  onChange={(e) => setFormData({...formData, precio_estimado: parseFloat(e.target.value) || ''})} 
                  className="w-full p-3 border-4 border-black" 
                />
                <input 
                  type="number" 
                  placeholder="Comisión Ofertada (%)" 
                  value={formData.comision_ofertada || ''} 
                  onChange={(e) => setFormData({...formData, comision_ofertada: parseFloat(e.target.value) || ''})} 
                  className="w-full p-3 border-4 border-black" 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <select 
                  value={formData.motivacion || 'media'} 
                  onChange={(e) => setFormData({...formData, motivacion: e.target.value})} 
                  className="w-full p-3 border-4 border-black font-bold"
                >
                  <option value="alta">🔴 Motivación ALTA</option>
                  <option value="media">🟡 Motivación MEDIA</option>
                  <option value="baja">🟢 Motivación BAJA</option>
                </select>
                
                <select 
                  value={formData.estado || 'nuevo'} 
                  onChange={(e) => setFormData({...formData, estado: e.target.value})} 
                  className="w-full p-3 border-4 border-black font-bold"
                >
                  <option value="nuevo">🆕 Nuevo</option>
                  <option value="contactado">📞 Contactado</option>
                  <option value="interesado">⭐ Interesado</option>
                  <option value="visita_agendada">📅 Visita Agendada</option>
                  <option value="exclusiva_firmada">✍️ Exclusiva Firmada</option>
                  <option value="descartado">❌ Descartado</option>
                </select>
              </div>
              
              <textarea 
                placeholder="Notas adicionales..." 
                value={formData.notes || ''} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                className="w-full p-3 border-4 border-black h-24" 
              />
            </>
          )}
          
          {activeTab === 'buyers' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="text" 
                  placeholder="*Nombre" 
                  value={formData.nombre || ''} 
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                  required
                />
                <input 
                  type="tel" 
                  placeholder="Teléfono" 
                  value={formData.telefono || ''} 
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                />
              </div>
              
              <input 
                type="email" 
                placeholder="Email" 
                value={formData.email || ''} 
                onChange={(e) => setFormData({...formData, email: e.target.value})} 
                className="w-full p-3 border-4 border-black" 
              />
              
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="number" 
                  placeholder="Presupuesto (€)" 
                  value={formData.presupuesto || ''} 
                  onChange={(e) => setFormData({...formData, presupuesto: parseFloat(e.target.value) || ''})} 
                  className="w-full p-3 border-4 border-black" 
                />
                <input 
                  type="text" 
                  placeholder="Zona Preferida" 
                  value={formData.zona || ''} 
                  onChange={(e) => setFormData({...formData, zona: e.target.value})} 
                  className="w-full p-3 border-4 border-black" 
                />
              </div>
              
              <select 
                value={formData.tipo_propiedad || ''} 
                onChange={(e) => setFormData({...formData, tipo_propiedad: e.target.value})} 
                className="w-full p-3 border-4 border-black"
              >
                <option value="">Tipo Propiedad Buscada</option>
                <option value="piso">Piso</option>
                <option value="casa">Casa</option>
                <option value="chalet">Chalet</option>
                <option value="duplex">Dúplex</option>
                <option value="atico">Ático</option>
              </select>
              
              <div className="grid grid-cols-2 gap-4">
                <select 
                  value={formData.urgencia || 'media'} 
                  onChange={(e) => setFormData({...formData, urgencia: e.target.value})} 
                  className="w-full p-3 border-4 border-black font-bold"
                >
                  <option value="alta">🔴 Urgencia ALTA</option>
                  <option value="media">🟡 Urgencia MEDIA</option>
                  <option value="baja">🟢 Urgencia BAJA</option>
                </select>
                
                <select 
                  value={formData.estado || 'nuevo'} 
                  onChange={(e) => setFormData({...formData, estado: e.target.value})} 
                  className="w-full p-3 border-4 border-black font-bold"
                >
                  <option value="nuevo">🆕 Nuevo</option>
                  <option value="contactado">📞 Contactado</option>
                  <option value="interesado">⭐ Interesado</option>
                  <option value="descartado">❌ Descartado</option>
                </select>
              </div>
              
              <textarea 
                placeholder="Notas adicionales..." 
                value={formData.notes || ''} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                className="w-full p-3 border-4 border-black h-24" 
              />
            </>
          )}
          
          {activeTab === 'properties' && (
            <>
              <input type="text" placeholder="Dirección" value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="text" placeholder="Nombre Propietario" value={formData.owner_name || ''} onChange={(e) => setFormData({...formData, owner_name: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="text" placeholder="Contacto Propietario" value={formData.owner_contact || ''} onChange={(e) => setFormData({...formData, owner_contact: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="text" placeholder="Tipo de Propiedad" value={formData.property_type || ''} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="number" placeholder="Precio Estimado" value={formData.estimated_price || ''} onChange={(e) => setFormData({...formData, estimated_price: e.target.value})} className="w-full p-3 border-4 border-black" />
              <textarea placeholder="Notas" value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full p-3 border-4 border-black h-24" />
            </>
          )}
          
          {activeTab === 'professionals' && (
            <>
              <input type="text" placeholder="Nombre Comercial" value={formData.nombre_comercial || ''} onChange={(e) => setFormData({...formData, nombre_comercial: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="text" placeholder="Responsable" value={formData.responsable || ''} onChange={(e) => setFormData({...formData, responsable: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="email" placeholder="Email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="tel" placeholder="Teléfono" value={formData.telefono || ''} onChange={(e) => setFormData({...formData, telefono: e.target.value})} className="w-full p-3 border-4 border-black" />
              <input type="text" placeholder="Domicilio" value={formData.domicilio || ''} onChange={(e) => setFormData({...formData, domicilio: e.target.value})} className="w-full p-3 border-4 border-black" />
              <textarea placeholder="Notas" value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full p-3 border-4 border-black h-24" />
            </>
          )}
          
          <div className="flex gap-4 pt-4">
            <button onClick={onSubmit} className="flex-1 bg-yellow-500 text-black p-4 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition text-lg">
              ✅ CREAR
            </button>
            <button onClick={onClose} className="flex-1 bg-gray-200 text-black p-4 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition text-lg">
              ❌ CANCELAR
            </button>
          </div>
        </div>
        
        {(activeTab === 'sellers' || activeTab === 'buyers') && (
          <div className="mt-4 p-4 bg-blue-100 border-4 border-blue-500 rounded">
            <p className="text-blue-900 font-bold text-sm">
              ℹ️ <strong>Sistema de alertas automático:</strong><br/>
              • Nuevo → Alerta cada 48h durante 1 mes<br/>
              • Contactado/Interesado → Alerta semanal durante 1 mes<br/>
              • Visita Agendada → Recordatorio a la semana<br/>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};